/**************************************
 ** AtlasViewer - SINGLETON
 *
 *  Main viewer class, based on configuration passed in, the viewer should be able to create a suitable
 *  display.  Primary use of the viewer is to determine what image to view and then display it in the
 *  correct area (status=sphere, viewer=canvas, div=div area).  The viewer should degrade gracefully.
 *
 *  Latitudes - the number of latitudes for the atlas (horizontal from top to bottom)
 *  Longitudes - the number of longitudes for the atlas (vertical from front facing to just left of front)
 *  ImageList - array of images that may be requested from the sphere
 *  StatusId - the area on the screen that will display the sphere (if null then the sphere will not be rendered)
 *  ViewerId - the canvas or div on the screen where the images will be displayed
 *
 *  CurrentSphereCell - the object containing the current intersection of the camera view point and the sphere
 *  ThresholdType - the type of threshold which dictates how the viewer will pick the next image
 *    - THRESHOLD_IMMEDIATE - show the image dictated by the current intersection point
 *    - THRESHOLD_STATIC - combined with StaticThreshold, this will show the image only after the intersection point
 *                         has reached the static distance into the quad of the sphere
 *    - THRESHOLD_ADAPTIVE - combined with AdaptiveThreshold, this will show the image only after the intersection point
 *                           has passed the percentage*minimum length of the edges that make up the quad
 *  StaticThreshiold - the static threshold used when ThresholdType==THRESHOLD_STATIC
 *  AdaptiveThreshold - the adapative threshold percentage used when ThresholdType==THRESHOLD_ADAPTIVE
 *
 *  The threshold type for the triangles (at the top and bottom of the sphere) is always immediate.
 ***************************************/
define('atlas/AtlasViewer', ['atlas/AtlasClientCapabilities', 'atlas/AtlasConstants', 'atlas/AtlasControlAdapter', 'atlas/AtlasControls', 'atlas/AtlasApplication'], function(AtlasClientCapabilities, Constants, AtlasControlAdapter, AtlasControls, Application) {

    'use strict';

    var AtlasViewer = function() {

        this._thresholdType = Constants.THRESHOLD_IMMEDIATE;
        this._staticThreshold = 50;
        this._adaptiveThreshold = 0.85;

        // this._viewerId = null;
        // this._controls = null;

        // this._viewerElement = null;
        // this._controlElement = null;
        // //if rendering to canvas then this will be the context
        // this._context = null;

        // this._sphere = null;

        // //the current image index
        // this._currentImageIndex = null;
        // //the next image index that will be displayed after tick
        // this._nextImageIndex = null;
        // this._imageCache = null;

        // //store the highest and lowest loaded latitude
        // this._highestAvailableLatitude = null;
        // this._lowestAvailableLatitude = null;

        //auto rotation state
        this._autoRotationInfo = {
            _rotating: false,
            _autoRotationFrame: 0
        };

        // this._onBeforeMouseMove = null;
        // this._onBeforeTouchMove = null;
        // this._onAfterImageChanged = null;
        this._onOneTimeRenderImageIndex = [];
        // this._onCanvasReady = null;

        this._firstRender = true;
        this._canvasIsDirty = true;
        this._frame = 0;
        this._framesPerUpdate = 1000;

        this._animationEnabled = false;

        this._forceCanvasRefresh = false;

        this._stopRendering = false;

        this._lastRenderTime = new Date();

        this._errorHandler = null;

        this._availableLatitudes = [];
        this._availableLongitudes = [];
        this._currentMappedImageIndex = null;

        this._updateViewerElementTimeout = null;
    };

    AtlasViewer.getInstance = function() {
        if (!AtlasViewer.instance) {
            AtlasViewer.instance = new AtlasViewer();
        }
        return AtlasViewer.instance;
    };

    AtlasViewer.refreshInstance = function() {
        AtlasViewer.instance = new AtlasViewer();
        return AtlasViewer.instance;
    };

    AtlasViewer.prototype.getCurrentImageIndex = function() {
        return this._currentImageIndex;
    };

    AtlasViewer.prototype.getCurrentMappedImageIndex = function() {
        if (this._currentMappedImageIndex) {
            return this._currentMappedImageIndex;
        }
        return this._currentImageIndex;
    };

    AtlasViewer.prototype.getImageCache = function() {
        return this._imageCache;
    };

    AtlasViewer.prototype.setNextImageIndex = function(nextImageIndex) {
        //make sure that the latitude is between the high and the low
        if (!nextImageIndex) {
            return;
        }

        var nextImageLatitude = nextImageIndex.substring(0, 1);
        var nextImageLongitude = nextImageIndex.substring(1);

        if (nextImageLatitude < this._highestAvailableLatitude) {
            nextImageLatitude = this._highestAvailableLatitude;
        }
        if (nextImageLatitude > this._lowestAvailableLatitude) {
            nextImageLatitude = this._lowestAvailableLatitude;
        }

        this._nextImageIndex = (nextImageLatitude + nextImageLongitude);

        this._currentMappedImageIndex = this._sphere.getMappedImageIndex(this._nextImageIndex);

        this._canvasIsDirty = true;

        //GRN-1466 - flicker when resizing window
        //causing crashes in latest version of chrome - resize is now ugly, but crash appears to be fixed
        if (this._updateViewerElementTimeout) {
            clearTimeout(this._updateViewerElementTimeout);
        }
        var that = this;
        var updateImage = this._nextImageIndex;
        this._updateViewerElementTimeout = setTimeout(function() {
            that._updateViewerElement(updateImage);
        }, 200);

        if (this._onAfterImageChanged) {
            this._onAfterImageChanged(this._currentImageIndex, this._nextImageIndex);
        }
    };

    AtlasViewer.prototype.init = function(configuration, sphere, imageCache) {
        this._stopRendering = false;

        //initialize viewer
        this._viewerId = configuration._viewerId;
        this._controlId = configuration._controlId;
        this._dragId = configuration._dragId;
        this._thresholdType = configuration._thresholdType;
        this._staticThreshold = configuration._staticThreshold;
        this._adaptiveThreshold = configuration._adaptiveThreshold;

        //the element in which we will replace the image src
        // this._viewerElement = null;
        // this._controlElement = null;

        // this._highestAvailableLatitude = null;
        // this._lowestAvailableLatitude = null;

        //create the sphere
        this._sphere = sphere;
        this._imageCache = imageCache;

        //are we currently in autorotation
        this._autoRotation = false;
        this._autoRotationFrame = 0;
        this._autoRotationIncrement = 0.1;

        this._firstRender = true;

        //at high resolutions, should switch to immediate mode
        if ((sphere._latitudes >= 20) && (sphere._longitudes >= 20)) {
            this._thresholdType = Constants.THRESHOLD_IMMEDIATE;
        }

        this._initializeViewer(configuration);

        //init some of the flags
        this._canvasIsDirty = true;
        this._animationEnabled = false;

        this._currentImageIndex = configuration._initialImageIndex;
        this._nextImageIndex = configuration._initialImageIndex;

        this._availableLatitudes = [];
        this._availableLongitudes = [];

        //begin the ticks
        Application._tick();
    };

    AtlasViewer.prototype.reinit = function(sphere, imageCache) {
        this._stopRendering = false;

        this._sphere = sphere;
        this._imageCache = imageCache;

        this._highestAvailableLatitude = null;
        this._lowestAvailableLatitude = null;

        //auto rotation state
        this._autoRotation = false;
        this._autoRotationFrame = 0;
        this._autoRotationPercentage = 0.0;

        //if animation is enabled
        this._firstRender = true;
        this._canvasIsDirty = true;
        this._animationEnabled = false;

        this._availableLatitudes = [];
        this._availableLongitudes = [];

        //this._currentImageIndex=null;
        //this._nextImageIndex=null;
        Application._tick();
    };

    AtlasViewer.prototype.rotate = function(horizontal, vertical) {
        if (!this._currentImageIndex) {
            return;
        }

        //take the current image index and split into lat and long
        var latitude = this._currentImageIndex.substring(0, 1);
        var longitude = this._currentImageIndex.substring(1);
        //map to the correct index
        var latitudeIndex = Constants.latitudeMarkers.indexOf(latitude);
        var longitudeIndex = Constants.longitudeMarkers.indexOf(longitude);
        //add the horizontal and vertical movements
        latitudeIndex += vertical; //can't rotate over poles so not an issue here
        longitudeIndex += (horizontal % this._sphere._longitudes); //keep witin a single revolution

        //bound to the current sphere (latitudes don't wrap since we can't go over poles)
        if (latitudeIndex < 0) {
            latitudeIndex = 0;
        }
        else if (latitudeIndex >= this._sphere._latitudes) {
            latitudeIndex = (this._sphere._latitudes - 1);
        }

        if (longitudeIndex < 0) {
            longitudeIndex += this._sphere._longitudes;
        }
        else if (longitudeIndex >= this._sphere._longitudes) {
            longitudeIndex -= this._sphere._longitudes;
        }

        //reconstruct the image index and set it as next
        var imageIndex = Constants.latitudeMarkers[latitudeIndex] + Constants.longitudeMarkers[longitudeIndex];
        this.setNextImageIndex(imageIndex);
    };

    AtlasViewer.prototype.snapToImageIndex = function(imageIndex) {
        this.scrollToImageIndex(imageIndex, false);
    };

    AtlasViewer.prototype.animateScrollToImageIndex = function(imageIndex) {
        this.scrollToImageIndex(imageIndex, true);
    };

    AtlasViewer.prototype.scrollToImageIndex = function(imageIndex, animated) {
        //reset the current mouse position
        if (this._controls) {
            this._controls.reset();
        }

        if ((animated) && (!this._animationEnabled)) {
            animated = false;
        }

        var destinationCell = this._sphere.getSphereCellForIndex(imageIndex);
        if (!destinationCell) {
            //could not find valid face, staying at current location
            return;
        }

        //if we are not asked for animation or we don't have a current intersection, then just go to the point
        if (!animated || !this._currentImageIndex) {
            this.setNextImageIndex(destinationCell.originalImageIndex);
        }
        //otherwise, we are going to do an animation so don't set the next image index, just let the _animate function take care of it
        else {
            var currentFace = this._currentImageIndex;
            var targetFace = destinationCell.imageIndex;

            var latitudeStart = currentFace.substring(0, 1);
            var latitudeEnd = targetFace.substring(0, 1);
            var longitudeStart = currentFace.substring(1);
            var longitudeEnd = targetFace.substring(1);

            //calculate the index and the span
            var latitudeStartIndex = Constants.latitudeMarkers.indexOf(latitudeStart);
            var latitudeEndIndex = Constants.latitudeMarkers.indexOf(latitudeEnd);
            var longitudeStartIndex = Constants.longitudeMarkers.indexOf(longitudeStart);
            var longitudeEndIndex = Constants.longitudeMarkers.indexOf(longitudeEnd);
            //figure out the shortest path around the longitudes
            var steps = 0;
            var travelRight = true;
            var leftSlots = 0;
            var rightSlots = 0;

            if (longitudeEndIndex > longitudeStartIndex) {
                leftSlots = (this._sphere._longitudes - longitudeEndIndex) + longitudeStartIndex;
                rightSlots = (longitudeEndIndex - longitudeStartIndex);
            } else if (longitudeEndIndex < longitudeStartIndex) {
                leftSlots = (longitudeStartIndex - longitudeEndIndex);
                rightSlots = (this._sphere._longitudes - longitudeStartIndex) + longitudeEndIndex;
            }
            if (rightSlots < leftSlots) {
                travelRight = true;
                steps = rightSlots;
            } else {
                travelRight = false;
                steps = leftSlots;
            }

            var travelUp = (latitudeStartIndex > latitudeEndIndex);
            var latitudesTraveled = Math.abs(latitudeEndIndex - latitudeStartIndex);

            //figure out the long edge
            var latitudeIsLongEdge = (latitudesTraveled > steps);
            var shortEdgeRatio = (latitudeIsLongEdge ? steps / latitudesTraveled : latitudesTraveled / steps);

            this._autoRotationInfo = {
                _currentFace: currentFace,
                _targetFace: targetFace,
                _rotating: true,
                _autoRotationFrame: 0,
                _startTime: new Date(),
                _lastUpdateTime: new Date(),
                _latitudeStartIndex: latitudeStartIndex,
                _latitudeEndIndex: latitudeEndIndex,
                _longitudeStartIndex: longitudeStartIndex,
                _longitudeEndIndex: longitudeEndIndex,
                _longEdge: latitudeIsLongEdge ? "latitude" : "longitude",
                _shortEdgeRatio: shortEdgeRatio,
                _travelRight: travelRight,
                _travelUp: travelUp,
                _currentLatitudeIndex: latitudeStartIndex,
                _currentLongitudeIndex: longitudeStartIndex
            };
        }
    };

    AtlasViewer.prototype.setTopLatitude = function(latitude) {
        this._highestAvailableLatitude = latitude;
    };

    AtlasViewer.prototype.getTopLatitude = function() {
        return this._highestAvailableLatitude;
    };

    AtlasViewer.prototype.setBottomLatitude = function(latitude) {
        this._lowestAvailableLatitude = latitude;
    };

    AtlasViewer.prototype.getBottomLatitude = function() {
        return this._lowestAvailableLatitude;
    };

    AtlasViewer.prototype.enableLatitude = function(latitude) {
        if (this._availableLatitudes.indexOf(latitude) < 0) {
            this._availableLatitudes.push(latitude);
            this._availableLatitudes.sort();
        }
    };

    AtlasViewer.prototype.enableLongitude = function(longitude) {
        if (this._availableLongitudes.indexOf(longitude) < 0) {
            this._availableLongitudes.push(longitude);
            this._availableLongitudes.sort();
        }
    };

    AtlasViewer.prototype.isLatitudeEnabled = function(latitude) {
        return (this._availableLatitude.indexOf(latitude) >= 0);
    };

    AtlasViewer.prototype.isLongitudeEnabled = function(longitude) {
        return (this._availableLongitudes.indexOf(longitude) >= 0);
    };

    AtlasViewer.prototype.getAvailableLatitudes = function() {
        return this._availableLatitudes;
    };

    AtlasViewer.prototype.getAvailableLongitudes = function() {
        return this._availableLongitudes;
    };

    AtlasViewer.prototype.setAnimationEnabled = function(animationEnabled) {
        this._animationEnabled = animationEnabled;
    };

    AtlasViewer.prototype.setErrorHandler = function(callback) {
        this._errorHandler = callback;
    };

    AtlasViewer.prototype.onImageLoaded = function(image) {};

    AtlasViewer.prototype.onBatchLoaded = function(batch) {};

    AtlasViewer.prototype.onLoadingComplete = function() {};

    AtlasViewer.prototype.setOnBeforeMouseMove = function(callback) {
        this._onBeforeMouseMove = callback;
        //set the value on the controls if we are already initialized
        if (this._controls) {
            this._controls.setOnBeforeMouseMove(this._onBeforeMouseMove);
        }
    };

    AtlasViewer.prototype.setOnBeforeTouchMove = function(callback) {
        this._onBeforeTouchMove = callback;
        //set the value on the controls if we are already initialized
        if (this._controls) {
            this._controls.setOnBeforeTouchMove(this._onBeforeTouchMove);
        }
    };

    AtlasViewer.prototype.setOnBeforeMouseDown = function(callback) {
        this._onBeforeMouseDown = callback;
        if (this._controls) {
            this._controls.setOnBeforeMouseDown(this._onBeforeMouseDown);
        }
    };

    AtlasViewer.prototype.setOnBeforeTouchDown = function(callback) {
        this._onBeforeTouchDown = callback;
        if (this._controls) {
            this._controls.setOnBeforeTouchDown(this._onBeforeTouchDown);
        }
    };

    AtlasViewer.prototype.setOnBeforeMouseUp = function(callback) {
        this._onBeforeMouseUp = callback;
        if (this._controls) {
            this._controls.setOnBeforeMouseUp(this._onBeforeMouseUp);
        }
    };

    AtlasViewer.prototype.setOnBeforeTouchEnd = function(callback) {
        this._onBeforeTouchEnd = callback;
        if (this._controls) {
            this._controls.setOnBeforeTouchEnd(this._onBeforeTouchEnd);
        }
    };

    AtlasViewer.prototype.setOnBeforeMouseOut = function(callback) {
        this._onBeforeMouseOut = callback;
        if (this._controls) {
            this._controls.setOnBeforeMouseOut(this._onBeforeMouseOut);
        }
    };

    AtlasViewer.prototype.setOnAfterImageChanged = function(callback) {
        this._onAfterImageChanged = callback;
    };

    AtlasViewer.prototype.onImageNotAvailable = function(imageIndex) {};

    AtlasViewer.prototype.setOnOneTimeRenderImageIndex = function(imageIndex, object, callback) {
        this._onOneTimeRenderImageIndex[imageIndex] = {
            object: object,
            callback: callback
        };
    };

    AtlasViewer.prototype.setOnCanvasReady = function(callback) {
        this._onCanvasReady = callback;
    };

    AtlasViewer.prototype.disableControls = function() {
        this._controls.disable();
    };

    AtlasViewer.prototype.enableControls = function() {
        this._controls.enable();
    };

    AtlasViewer.prototype.controlsClickDrag = function(clientX, clientY) {
        this._controls.clickDrag(clientX, clientY);
    };

    AtlasViewer.prototype.enableInteractivity = function() {
        var capabilities = AtlasClientCapabilities.getCapabilities();
        //if we are rendering to canvas, then show the canvas
        if (capabilities.renderToCanvas) {
            this._viewerElement.style.display = 'block';
        }
        //GRN-1554 - nexus completely broken (this needs to be ported over to hero player)
        var re = new RegExp('Nexus');
        if (re.exec(navigator.userAgent) !== null) {
            document.addEventListener('touchstart', function(e) { /*noop*/ });
        }

        //enable the controls regardless of environment
        this._controls.enable();
    };

    AtlasViewer.prototype.disableInteractivity = function() {
        var capabilities = AtlasClientCapabilities.getCapabilities();
        if (capabilities.renderToCanvas) {
            this._viewerElement.style.display = 'none';
        }
        this._controls.disable();
    };

    AtlasViewer.prototype.setForceCanvasRefresh = function(forceRefresh) {
        this._forceCanvasRefresh = forceRefresh;
    };

    AtlasViewer.prototype.forceResizeRefresh = function() {
        //related to GRN-188 where canvas was not displaying images after sleep (usually with low battery)
        //I could not figure out how to detect it, but could figure out that this fixed it.  Currently
        //we check if the last render time was > 30secs and if so then force a resize / refresh in the 
        //_tick function
        if (this._viewerElement) {
            this._viewerElement.width = this._viewerElement.width - 1;
            this._viewerElement.width = this._viewerElement.width + 1;
            this._forceCanvasRefresh = true;
        }
    };

    AtlasViewer.prototype.stopRendering = function() {
        this._stopRendering = true;
    };

    AtlasViewer.prototype.resumeRendering = function() {
        this._stopRendering = false;
    };

    AtlasViewer.prototype.getCanvasRatio = function() {
        var devicePixelRatio = window.devicePixelRatio || 1;
        var backingStoreRatio = this._context.webkitBackingStorePixelRatio ||
                           this._context.mozBackingStorePixelRatio ||
                           this._context.msBackingStorePixelRatio ||
                           this._context.oBackingStorePixelRatio ||
                           this._context.backingStorePixelRatio || 1;
        var ratio = devicePixelRatio / backingStoreRatio;
        return ratio;
     };

    AtlasViewer.prototype._initializeViewer = function(configuration) {
        //NEEDS WORK: should be if it is not undefined and loaded
        if (configuration._initialImageIndex) {
            this.scrollToImageIndex(configuration._initialImageIndex, false);
        }

        var capabilities = AtlasClientCapabilities.getCapabilities();

        //the current display - need to add option for just displaying in a div
        if (this._viewerId) {
            if (capabilities.renderToCanvas) {
                //create a canvas element attached to the viewer id
                var containingElement = document.getElementById(this._viewerId);
                if (containingElement.parentNode) {
                    containingElement = containingElement.parentNode;
                }

                //if we are in canvas, then we can set the size of the area to be different than the size of the canvas and it
                //should scale correctly
                this._viewerElement = document.createElement('canvas');
                this._viewerElement.style.position = 'absolute';
                this._viewerElement.style.top = 0;
                this._viewerElement.style.left = 0;
                this._viewerElement.style.zIndex = 0;

                window.addEventListener('resize', function() {
                    AtlasViewer.getInstance().forceResizeRefresh();
                }, false);

                this._context = this._viewerElement.getContext('2d');

                var devicePixelRatio = window.devicePixelRatio || 1;
                var backingStoreRatio = this._context.webkitBackingStorePixelRatio ||
                                        this._context.mozBackingStorePixelRatio ||
                                        this._context.msBackingStorePixelRatio ||
                                        this._context.oBackingStorePixelRatio ||
                                        this._context.backingStorePixelRatio || 1;
                var ratio = devicePixelRatio / backingStoreRatio;

                //set the canvas size based on the resolution
                this._viewerElement.width = ratio * AtlasClientCapabilities.getCapabilities().windowDimensions.width;
                this._viewerElement.height = ratio * AtlasClientCapabilities.getCapabilities().windowDimensions.width;

                //set the css style dimensions based on the specified windowDimensions
                this._viewerElement.style.width = AtlasClientCapabilities.getCapabilities().windowDimensions.width + 'px';
                this._viewerElement.style.height = AtlasClientCapabilities.getCapabilities().windowDimensions.height + 'px';
                this._viewerElement.style.display = 'none';

                containingElement.appendChild(this._viewerElement);
            } else {
                //just pull the viewer id, and render inside of that
                this._viewerElement = document.getElementById(this._viewerId);
                //if we are rendering inside the div, then it SHOULD be properly sized already, however we will set max-width and max-height
                //in the image rendering code
            }

            //control is where we will detect the initial touch/mouse/pointer down events
            this._controlElement = document.getElementById(this._controlId);
            //drag is where we will track the move and end events
            this._dragElement = document.getElementById(this._dragId);

            if (!this._viewerElement) {
                //viewer element could not be located
            } else if (!this._controlElement) {
                //control element could not be located
            } else {
                this._controlAdapter = new AtlasControlAdapter(this._context);
                this._controls = new AtlasControls(this._controlElement, this._controlAdapter, this._dragElement, configuration._hideCursor);
                //wait for interactivity
                this._controls.disable();

                // TODO - fix circular dep
                // http://requirejs.org/docs/api.html#circular
                Application = require('atlas/AtlasApplication');

                //add an event listener for control change events
                if (capabilities.eventListener) {
                    this._controlElement.addEventListener('change', Application._onChange, false);
                } else {
                    if (capabilities.jqueryEvents) {
                        //support for jquery prior to 1.7
                        if (typeof jQuery(document).on === 'undefined') {
                            jQuery(document).bind('change', Application._onChange);
                        }
                        //support for jquery 1.7 and later
                        else {
                            jQuery(document).on('change', Application._onChange);
                        }
                    } else {
                        //jQuery has not been loaded, and browser does not support custom events
                        return false;
                    }
                }

                //setup event handlers
                if (this._onBeforeMouseMove) {
                    this._controls.setOnBeforeMouseMove(this._onBeforeMouseMove);
                }
                if (this._onBeforeTouchMove) {
                    this._controls.setOnBeforeTouchMove(this._onBeforeTouchMove);
                }

                //setup event handler to cancel autoration if needed
                this._controls.setOnBeforeMouseDown(function(event) {
                    //cancelling auto rotation
                    if (this._autoRotationInfo) {
                        this._autoRotationInfo._rotating = false;
                    }

                    return true;
                });
            }
        }
    };

    AtlasViewer.prototype._updateViewerElement = function(imageIndex) {
        //keep the base image synched (to help with stutter on resize GRN-1466)
        var capabilities = AtlasClientCapabilities.getCapabilities();
        if ((capabilities.renderToCanvas) && (this._viewerId)) {
            var image=this._imageCache.fetch(imageIndex);
            if (image && image.htmlImage) {
                document.getElementById(this._viewerId).innerHTML='<img style="max-width:100%; max-height:100%" src=' + image.htmlImage.src + '>';
            }
        }
    };

    AtlasViewer.prototype.getControlAdapter = function() {
        return this._controlAdapter;
    };

    return AtlasViewer;
});
